import React, { useState } from "react";
import { X } from "lucide-react";
import apiClient from "../services/api";
import { pushRedditConversionEvent } from "../utils/redditTracking";

const STARS = [1, 2, 3, 4, 5];

const AppFeedbackModal = ({ open, onClose, onSubmitted, userEmail, userName }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!rating) {
      setError("Please select a rating.");
      return;
    }
    if (!userEmail) {
      setError("Please sign in with your email before submitting feedback.");
      return;
    }
    setError("");
    pushRedditConversionEvent({
      event: "feedback_submit",
      userData: { email_address: userEmail },
      eventData: {
        action: "feedback_submit",
        rating,
        has_comments: Boolean(comments && comments.trim().length > 0),
        comment_length: comments.trim().length,
      },
    });
    setIsSubmitting(true);
    const messageBody = `Rating: ${rating}/5\n\nFeedback:\n${comments || "No comments provided."}`;
    try {
      await apiClient.post("/send-feedback", {
        user_email: userEmail,
        message: messageBody,
      });
      setIsSubmitting(false);
      setRating(0);
      setHoverRating(0);
      setComments("");
      onSubmitted?.();
      onClose?.();
      alert("Thanks for your feedback! Your message has been sent.");
    } catch (err) {
      setIsSubmitting(false);
      const detail = err.response?.data?.detail || err.message;
      setError(detail || "Failed to send feedback. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl p-6">
        <button
          type="button"
          aria-label="Close feedback"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="font-unbounded text-xl text-gray-900">Leave a review</h3>
        <p className="mt-1 text-sm text-gray-700">Please give us feedback</p>

        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          <div className="flex gap-2">
            {STARS.map((star) => {
              const active = star <= (hoverRating || rating);
              return (
                <button
                  key={star}
                  type="button"
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(star)}
                  className="focus:outline-none"
                  aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="w-7 h-7"
                    fill={active ? "#F59E0B" : "none"}
                    stroke="#F59E0B"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.98 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557L3.04 10.385a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                    />
                  </svg>
                </button>
              );
            })}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">
              Do you have any thoughts to share?
            </label>
            <textarea
              value={comments}
              onChange={(event) => setComments(event.target.value)}
              rows={4}
              placeholder="Tell us what’s working well or what could be better."
              className="w-full rounded-xl border border-gray-200 bg-[#FFF8F4] p-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#F07639]"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-full bg-[#F07639] text-white py-3 font-semibold hover:bg-[#E15F1F] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AppFeedbackModal;
